// client/src/coms/views/canvas_parts/party/usePartySession.js
// Mirrors one party game session from its live stream, sends actions (with ids, so retries are safe) and
// keeps a cache of drawings that live pictures (and older stroke relays) update in place. The server
// decides every rule.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyPicture, applyRelayOp, createModel } from './partyDrawingModel';

const ERRORS = {
  host_only: 'Only the host can do that.',
  not_enough_players: 'More players are needed to start.',
  too_many_players: 'There are too many players for this game.',
  players_disconnected: 'Wait until everyone is connected.',
  players_not_ready: 'Everyone needs to be ready first.',
  session_full: 'This game is full.',
  game_in_progress: 'This game has already started.',
  no_game_running: 'No game is running.',
  game_not_finished: 'The game is not over yet.',
  removed_by_host: 'The host removed you from this game.',
  session_not_found: 'This game is no longer available.',
  invalid_code: 'That invite code does not match an open game.',
  too_many_sessions: 'You are already hosting the maximum number of games.',
  not_a_server_member: 'You are not a member of that server.',
  not_a_canvas_member: 'You are not a member of that canvas.',
  invite_friends_only: 'You can only invite friends.',
  slow_down: 'Slow down a little.',
  blocked_words: 'Please keep it family friendly.',
  text_too_long: 'That is too long.',
  empty_text: 'Write something first.',
  too_close: "That's the real answer! Write a convincing fake instead.",
  cannot_vote_own: "You can't vote for your own entry.",
  already_guessed: 'You already got it!',
  drawer_cannot_guess: 'You are the artist this turn.',
  stale_phase: 'That was for an earlier step.',
  too_late: 'Too late: time is up.',
  drawing_closed: 'Time is up for this drawing.',
  invalid_picture: 'That picture could not be sent.',
  picture_too_large: 'That picture is too large to send.',
  straight_line: 'Straight lines are not allowed this round.',
  not_your_drawing: 'This is not your drawing.',
  stroke_limit: 'No strokes left.',
  undo_disabled: 'Undo is disabled this round.',
  clear_disabled: 'Clearing is disabled this round.',
  tool_not_allowed: 'That tool is not allowed right now.',
  color_not_allowed: 'That colour is not allowed right now.',
  size_not_allowed: 'That brush size is not allowed right now.',
  spectators_disabled: 'Spectators are turned off for this game.',
  invalid_setting: 'That setting is not valid.',
  chat_in_game: 'Chat opens again after the game.',
  not_playing: 'You are watching this game.',
  invalid_team: 'Pick a valid team.',
};

export function describePartyError(code) {
  if (ERRORS[code]) return ERRORS[code];
  return 'Something went wrong. Try again.';
}

export async function partyApi(path, { method = 'GET', body } = {}) {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
}

let actionCounter = 0;
export function newActionId() {
  actionCounter += 1;
  return `${Date.now().toString(36)}-${actionCounter}-${Math.random().toString(36).slice(2, 7)}`;
}

// A store of drawing models with subscriptions; one per session.
export function createDrawingStore(sessionId) {
  const models = new Map();
  const listeners = new Map();
  const pending = new Set();
  let timer = null;

  function notify(id) {
    const set = listeners.get(id);
    if (set) set.forEach((fn) => fn());
  }

  function flushFetch() {
    timer = null;
    const ids = Array.from(pending);
    pending.clear();
    if (!ids.length || !sessionId) return;
    for (let i = 0; i < ids.length; i += 40) {
      const chunk = ids.slice(i, i + 40);
      partyApi(`/api/party/sessions/${encodeURIComponent(sessionId)}/drawings?ids=${chunk.map(encodeURIComponent).join(',')}`).then(({ ok, data }) => {
        if (!ok) return;
        (data.drawings || []).forEach((snapshot) => {
          models.set(snapshot.id, createModel(snapshot));
          notify(snapshot.id);
        });
      }).catch(() => {});
    }
  }

  function request(id) {
    if (!id) return;
    pending.add(id);
    if (!timer) timer = setTimeout(flushFetch, 30);
  }

  return {
    get: (id) => models.get(id) || null,
    request,
    refresh(id) {
      request(id);
    },
    subscribe(id, fn) {
      if (!listeners.has(id)) listeners.set(id, new Set());
      listeners.get(id).add(fn);
      return () => listeners.get(id) && listeners.get(id).delete(fn);
    },
    applyRelay(event) {
      const model = models.get(event.drawingId);
      if (!model) {
        request(event.drawingId);
        return;
      }
      if (event.from > model.seq) {
        // Missed something: take a fresh copy.
        request(event.drawingId);
        return;
      }
      event.ops.slice(model.seq - event.from).forEach((op) => applyRelayOp(model, op));
      model.seq = Math.max(model.seq, event.to);
      notify(event.drawingId);
    },
    // A writer's picture, live from the Canvas editor.
    applyPicture(event) {
      const model = models.get(event.drawingId);
      if (!model) {
        request(event.drawingId);
        return;
      }
      if (applyPicture(model, event.picture)) notify(event.drawingId);
    },
    // Static drawings (saved galleries).
    seed(map) {
      Object.entries(map || {}).forEach(([id, value]) => {
        models.set(id, createModel({ id, seq: 0, items: value.items || [], base: value.base || null, pictures: value.pictures || [], basePictures: value.basePictures || [] }));
        notify(id);
      });
    },
  };
}

export function isNewerView(current, next) {
  if (!next || typeof next !== 'object') return false;
  if (!current || current.id !== next.id) return true;
  return Number(next.rev) >= Number(current.rev);
}

export default function usePartySession(sessionId) {
  const [view, setView] = useState(null);
  const [closed, setClosed] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const viewRef = useRef(null);
  const offsetRef = useRef(0);
  const store = useMemo(() => createDrawingStore(sessionId), [sessionId]);

  const apply = useCallback((next) => {
    if (!isNewerView(viewRef.current, next)) return;
    if (next.serverNow) offsetRef.current = next.serverNow - Date.now();
    viewRef.current = next;
    setView(next);
  }, []);

  const load = useCallback(() => {
    if (!sessionId) return Promise.resolve();
    return partyApi(`/api/party/sessions/${encodeURIComponent(sessionId)}`).then(({ ok, status, data }) => {
      if (ok) apply(data.view);
      else if (status === 404) setClosed('session_not_found');
    }).catch(() => {});
  }, [apply, sessionId]);

  useEffect(() => {
    viewRef.current = null;
    setView(null);
    setClosed('');
    load();
  }, [load]);

  // The live stream opens once the user is a member (players and spectators, not visitors).
  const isMember = Boolean(view && view.me && view.me.role !== 'visitor');
  useEffect(() => {
    if (!sessionId || !isMember || typeof window === 'undefined' || typeof window.EventSource === 'undefined') return undefined;
    let retry = null;
    const source = new window.EventSource(`/api/party/sessions/${encodeURIComponent(sessionId)}/events`, { withCredentials: true });
    source.onmessage = (message) => {
      try {
        const event = JSON.parse(String(message.data || '{}'));
        if (event.type === 'party_view') apply(event.view);
        else if (event.type === 'party_draw') store.applyRelay(event);
        else if (event.type === 'party_picture') store.applyPicture(event);
        else if (event.type === 'party_closed') {
          setClosed('session_not_found');
          source.close();
        } else if (event.type === 'party_removed') {
          setClosed('removed_by_host');
          source.close();
        }
      } catch {}
    };
    source.onerror = () => {
      // The browser reconnects by itself; refresh the snapshot so nothing is missed meanwhile.
      if (retry) return;
      retry = setTimeout(() => {
        retry = null;
        load();
      }, 2500);
    };
    return () => {
      if (retry) clearTimeout(retry);
      source.close();
    };
  }, [apply, isMember, load, sessionId, store]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = window.setTimeout(() => setError(''), 3500);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 2500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const act = useCallback(async (action, payload = {}, { quietErrors = false } = {}) => {
    if (!sessionId) return null;
    const current = viewRef.current;
    const body = { ...payload, action, actionId: payload.actionId || newActionId() };
    if (current && current.phase && payload.phaseSeq === undefined && !['draw', 'react', 'leave', 'join', 'ready', 'configure', 'team', 'kick', 'transfer', 'role', 'chat', 'start', 'rematch', 'lobby', 'end', 'skip'].includes(action)) {
      body.phaseSeq = current.phase.seq;
    }
    try {
      let result = await partyApi(`/api/party/sessions/${encodeURIComponent(sessionId)}/actions`, { method: 'POST', body });
      if (result.status >= 500 || result.status === 0) {
        // One retry with the same action id: the server ignores it if the first attempt landed.
        result = await partyApi(`/api/party/sessions/${encodeURIComponent(sessionId)}/actions`, { method: 'POST', body });
      }
      if (result.data && result.data.view) apply(result.data.view);
      if (result.data && result.data.closed) setClosed('left');
      if (result.data && result.data.left) setClosed('left');
      if (!result.ok) {
        if (result.status === 404) setClosed('session_not_found');
        if (!quietErrors) setError(describePartyError(result.data && result.data.error));
      }
      return result;
    } catch {
      if (!quietErrors) setError('Could not reach the game server.');
      return null;
    }
  }, [apply, sessionId]);

  const serverNow = useCallback(() => Date.now() + offsetRef.current, []);

  return { sessionId, view, closed, error, notice, setError, setNotice, act, store, serverNow, reload: load };
}
